import memeService from "./../MemeService";
import notificationService from "./../NotificationService";
import { useMemo, useCallback } from "react";
import useEntityState from "./../entityTemplateReducer";
import { useTranslation } from "react-i18next";

const PreloadProfanityFilterService = () => {
    const { t } = useTranslation("preloadErrors");

    const {
        getEntities,
        deleteEntityState,
        setEntityState,
        updateEntityState,
        addEntityState,
        addFirstEntityState,
    } = useEntityState();

    const profanityFilters = useMemo(() => {
        return getEntities("profanityFilters");
    }, [getEntities]);

    const setProfanityFilters = useCallback(
        (filters) => {
            if (!filters)
                return console.warn(
                    "PreloadProfanityFilterService: setProfanityFilters missing filters",
                );

            // Do not use this to update 1 template
            setEntityState(filters, "profanityFilters");
        },
        [setEntityState],
    );

    const replaceProfanityFilter = useCallback(
        (filter) => {
            if (!filter)
                return console.warn(
                    "PreloadProfanityFilterService: replaceProfanityFilter missing filter",
                );
            updateEntityState(filter, "profanityFilters");
        },
        [updateEntityState],
    );

    const updateProfanityFilter = useCallback(
        async (filter, filterInput) => {
            if (!filter)
                return console.warn(
                    "PreloadProfanityFilterService: updateProfanityFilter missing filter",
                );

            let updatedFilter = {};

            try {
                const response = await memeService.updateProfanityFilter(
                    filter.id,
                    filterInput,
                );

                if (!response.id)
                    throw new Error("Could not update profanity filter!");

                updatedFilter = response;
            } catch (error) {
                notificationService.setMessage(
                    t("profanitySomethingWentWrongUpdating"),
                );
                console.error(
                    "PreloadProfanityFilterService: updateProfanityFilter ->",
                    error,
                );
                return false;
            }

            replaceProfanityFilter(filter);
            return updatedFilter;
        },
        [replaceProfanityFilter, t],
    );

    const profanityFilterAddLogic = async (newFilterInput) => {
        if (!newFilterInput)
            return console.warn(
                "PreloadProfanityFilterService: profanityFilterAddLogic missing input",
            );

        let addedFilter = {};

        try {
            addedFilter =
                await memeService.createProfanityFilter(newFilterInput);
            if (!addedFilter.id) throw new Error("NO FILTER ID");
        } catch (error) {
            notificationService.setMessage(
                t("profanitySomethingWentWrongAdding"),
            );
            console.error(
                "PreloadProfanityFilterService: profanityFilterAddLogic ->",
                error,
            );
            return false;
        }

        return addedFilter;
    };

    const pushProfanityFilter = useCallback(
        async (template, price) => {
            const addedFilter = await profanityFilterAddLogic(template, price);

            if (addedFilter) addEntityState(addedFilter, "profanityFilters");

            return addedFilter;
        },
        [addEntityState],
    );

    const addFirstProfanityFilter = useCallback(
        async (template, price) => {
            const addedFilter = await profanityFilterAddLogic(template, price);

            if (addedFilter)
                addFirstEntityState(addedFilter, "profanityFilters");

            return addedFilter;
        },
        [addFirstEntityState],
    );

    const deleteProfanityFilter = useCallback(
        async (filter) => {
            if (!filter || !filter.id)
                return console.warn(
                    "PreloadProfanityFilterService: deleteProfanityFilter missing filter",
                );

            try {
                const response = await memeService.deleteProfanityFilter(
                    filter.id,
                );
                if (!response.ok) throw new Error("Response was not ok");
            } catch (error) {
                notificationService.setMessage(
                    t("profanitySomethingWentWrongDeleting"),
                );
                console.error(
                    "PreloadProfanityFilterService: deleteProfanityFilter ->",
                    error,
                );
                return false;
            }

            deleteEntityState(filter, "profanityFilters");

            return true;
        },
        [deleteEntityState, t],
    );

    const loadProfanityFilter = useCallback(async () => {
        return await loadFilters(
            memeService.listAllProfanityFilters.bind(memeService),
            setProfanityFilters,
        );
    }, [setProfanityFilters]);

    const loadFilters = async (
        serviceFunction,
        setFilterState,
        disablePagination,
    ) => {
        let profanityFilterAccumulationMap = [];
        let paginationToken = null;

        try {
            // Start loading the templates
            do {
                const responseObject = await serviceFunction(paginationToken);

                const responseObjectSelector = responseObject.filters;

                // Accumulate templates
                profanityFilterAccumulationMap = [
                    ...profanityFilterAccumulationMap,
                    ...responseObjectSelector,
                ];

                // Update the state once with all the templates
                setFilterState(profanityFilterAccumulationMap);

                // Set the next pagination token if exists
                paginationToken = disablePagination
                    ? null
                    : responseObject.paginationToken;
            } while (paginationToken);
        } catch (error) {
            // In case of an error, notify the user and set the empty state
            notificationService.setMessage(
                t("profanitySomethingWentWrongLoading"),
            );
            console.error(
                "PreloadProfanityFilterService: loadFilters could not load filters !",
                error,
            );
            setFilterState(profanityFilterAccumulationMap);
        }

        return profanityFilterAccumulationMap;
    };

    return {
        loadProfanityFilter,
        deleteProfanityFilter,
        updateProfanityFilter,
        profanityFilters,
        addFirstProfanityFilter,
        pushProfanityFilter,
        replaceProfanityFilter,
    };
};

export default PreloadProfanityFilterService;
